import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { twoFAAPI } from '../services/api';

const TwoFactorSettings = ({ onClose }) => {
  const [status, setStatus] = useState({ enabled: false, email: '' });
  const [loading, setLoading] = useState(true);
  const [setupStep, setSetupStep] = useState('initial'); // initial, code-sent, verify, backup-codes
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [disablePassword, setDisablePassword] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const response = await twoFAAPI.getStatus();
      setStatus(response.data);
    } catch (error) {
      toast.error('Failed to load 2FA status');
    } finally {
      setLoading(false);
    }
  };

  const handleSendSetupCode = async () => {
    setProcessing(true);
    try {
      await twoFAAPI.sendSetupCode();
      setSetupStep('verify');
      toast.success('Verification code sent to your email');
    } catch (error) {
      toast.error('Failed to send verification code: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleVerifySetup = async () => {
    if (verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit verification code');
      return;
    }

    setProcessing(true);
    try {
      const response = await twoFAAPI.verifySetup(verificationCode);
      setBackupCodes(response.data.backupCodes);
      setSetupStep('backup-codes');
      toast.success('2FA enabled successfully!');
    } catch (error) {
      toast.error('Invalid verification code');
    } finally {
      setProcessing(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!disablePassword.trim()) {
      toast.error('Please enter your password');
      return;
    }

    setProcessing(true);
    try {
      await twoFAAPI.disable(disablePassword);
      setStatus({ ...status, enabled: false });
      setDisablePassword('');
      toast.success('2FA disabled successfully');
    } catch (error) {
      toast.error('Failed to disable 2FA: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleFinishSetup = () => {
    setStatus({ ...status, enabled: true });
    setSetupStep('initial');
    setVerificationCode('');
    setBackupCodes([]);
  };

  const downloadBackupCodes = () => {
    const content = `OUK Student Recruiters - Two-Factor Authentication Backup Codes
Generated: ${new Date().toLocaleString()}
Email: ${status.email}

IMPORTANT: Store these codes in a safe place. Each code can only be used once.

${backupCodes.map((code, index) => `${index + 1}. ${code}`).join('\n')}

Instructions:
- Use these codes if you can't access your email for 2FA verification
- Each code can only be used once
- Generate new codes if you lose these
- Keep them secure and don't share with anyone`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ouk-2fa-backup-codes-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="twofa-modal">
          <div className="twofa-loading">
            <div className="loading-spinner"></div>
            <h3>Loading 2FA Settings...</h3>
            <p>Please wait while we fetch your security settings</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="twofa-modal">
        {/* Header */}
        <div className="twofa-header">
          <div className="twofa-header-content">
            <div className="twofa-icon">🛡️</div>
            <div>
              <h2>Two-Factor Authentication</h2>
              <p>Enhance your account security with email verification</p>
            </div>
          </div>
          <button className="twofa-close" onClick={onClose}>
            <span>✕</span>
          </button>
        </div>

        {/* Status Card */}
        <div className={`twofa-status ${status.enabled ? 'enabled' : 'disabled'}`}>
          <div className="status-indicator">
            <div className={`status-dot ${status.enabled ? 'active' : 'inactive'}`}></div>
            <div className="status-info">
              <h3>2FA is {status.enabled ? 'Enabled' : 'Disabled'}</h3>
              <p>
                {status.enabled 
                  ? `Verification codes will be sent to ${status.email}`
                  : 'Enable 2FA to add an extra layer of security to your account'
                }
              </p>
            </div>
          </div>
          <div className="status-badge">
            {status.enabled ? (
              <span className="badge-enabled">🟢 Active</span>
            ) : (
              <span className="badge-disabled">🔴 Inactive</span>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="twofa-content">
          {!status.enabled && (
            <>
              {setupStep === 'initial' && (
                <div className="setup-initial">
                  <div className="setup-hero">
                    <div className="hero-icon">🔐</div>
                    <h3>Enable Two-Factor Authentication</h3>
                    <p>Add an extra layer of security to protect your account from unauthorized access.</p>
                  </div>

                  <div className="feature-list">
                    <div className="feature-item">
                      <div className="feature-icon">📧</div>
                      <div>
                        <h4>Email Verification</h4>
                        <p>Receive verification codes via email</p>
                      </div>
                    </div>
                    <div className="feature-item">
                      <div className="feature-icon">🔒</div>
                      <div>
                        <h4>Enhanced Security</h4>
                        <p>Protect against unauthorized logins</p>
                      </div>
                    </div>
                    <div className="feature-item">
                      <div className="feature-icon">🔑</div>
                      <div>
                        <h4>Backup Codes</h4>
                        <p>Recovery options if email is unavailable</p>
                      </div>
                    </div>
                  </div>

                  <div className="setup-steps">
                    <h4>How it works:</h4>
                    <div className="steps-list">
                      <div className="step">
                        <span className="step-number">1</span>
                        <span>Enter your username and password</span>
                      </div>
                      <div className="step">
                        <span className="step-number">2</span>
                        <span>Check your email for a verification code</span>
                      </div>
                      <div className="step">
                        <span className="step-number">3</span>
                        <span>Enter the code to complete login</span>
                      </div>
                    </div>
                  </div>

                  <button 
                    className="btn-primary-large"
                    onClick={handleSendSetupCode}
                    disabled={processing}
                  >
                    {processing ? (
                      <>
                        <div className="btn-spinner"></div>
                        Sending Code...
                      </>
                    ) : (
                      <>
                        <span>🚀</span>
                        Enable 2FA
                      </>
                    )}
                  </button>
                </div>
              )}

              {setupStep === 'verify' && (
                <div className="setup-verify">
                  <div className="verify-hero">
                    <div className="hero-icon">📧</div>
                    <h3>Check Your Email</h3>
                    <p>We've sent a 6-digit verification code to</p>
                    <div className="email-display">{status.email}</div>
                  </div>

                  <div className="code-input-section">
                    <label>Verification Code</label>
                    <input
                      type="text"
                      className="code-input"
                      placeholder="000000"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength="6"
                    />
                    <p className="input-help">Enter the 6-digit code from your email</p>
                  </div>

                  <div className="verify-actions">
                    <button 
                      className="btn-primary-large"
                      onClick={handleVerifySetup}
                      disabled={processing || verificationCode.length !== 6}
                    >
                      {processing ? (
                        <>
                          <div className="btn-spinner"></div>
                          Verifying...
                        </>
                      ) : (
                        <>
                          <span>✅</span>
                          Verify & Enable
                        </>
                      )}
                    </button>
                    <button 
                      className="btn-secondary"
                      onClick={() => setSetupStep('initial')}
                      disabled={processing}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {setupStep === 'backup-codes' && (
                <div className="setup-complete">
                  <div className="success-hero">
                    <div className="hero-icon success">🎉</div>
                    <h3>2FA Enabled Successfully!</h3>
                    <p>Your account is now more secure. Save these backup codes in a safe place.</p>
                  </div>

                  <div className="backup-codes-section">
                    <h4>Your Backup Codes</h4>
                    <div className="backup-codes-grid">
                      {backupCodes.map((code, index) => (
                        <div key={index} className="backup-code">
                          {code}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="backup-warning">
                    <div className="warning-icon">⚠️</div>
                    <div>
                      <h4>Important Security Notice</h4>
                      <p>Store these codes safely. Each code can only be used once and they're your backup if you can't access your email.</p>
                    </div>
                  </div>

                  <div className="complete-actions">
                    <button 
                      className="btn-secondary"
                      onClick={downloadBackupCodes}
                    >
                      <span>📥</span>
                      Download Codes
                    </button>
                    <button 
                      className="btn-primary-large"
                      onClick={handleFinishSetup}
                    >
                      <span>✅</span>
                      Complete Setup
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {status.enabled && (
            <div className="disable-section">
              <div className="disable-hero">
                <div className="hero-icon danger">🚨</div>
                <h3>Disable Two-Factor Authentication</h3>
                <p>Disabling 2FA will make your account less secure. You'll only need your password to log in.</p>
              </div>

              <div className="disable-form">
                <label>Confirm with your password</label>
                <input
                  type="password"
                  className="password-input"
                  placeholder="Enter your current password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                />
              </div>

              <button 
                className="btn-danger-large"
                onClick={handleDisable2FA}
                disabled={processing || !disablePassword.trim()}
              >
                {processing ? (
                  <>
                    <div className="btn-spinner"></div>
                    Disabling...
                  </>
                ) : (
                  <>
                    <span>🔓</span>
                    Disable 2FA
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TwoFactorSettings;